"use client";

import { getLocalStorage } from "./StorageHelper";

const COOKIE_VID = "vmb_vid";
const COOKIE_SID = "vmb_sid";
const VID_MAX_AGE = 395 * 24 * 60 * 60; // ~13 months
const SID_MAX_AGE = 30 * 60; // 30 min sliding

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, maxAgeSeconds) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax; Secure`;
}

function deleteCookie(name) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
}

function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function isTrackingEnabled() {
  return getLocalStorage("cookie_consent", null) === true;
}

function captureUtmAndSource() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);

  return {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
    utmContent: params.get("utm_content") || undefined,
    utmTerm: params.get("utm_term") || undefined,
    gclid: params.get("gclid") || undefined,
    gbraid: params.get("gbraid") || undefined,
    wbraid: params.get("wbraid") || undefined,
    landingPage: window.location.pathname,
    referrer: document.referrer || undefined,
  };
}

function ensureIds() {
  let visitorId = getCookie(COOKIE_VID);
  if (!visitorId) {
    visitorId = generateId();
    setCookie(COOKIE_VID, visitorId, VID_MAX_AGE);
  }

  let sessionId = getCookie(COOKIE_SID);
  const isNewSession = !sessionId;
  if (isNewSession) {
    sessionId = generateId();
  }
  setCookie(COOKIE_SID, sessionId, SID_MAX_AGE);

  return { visitorId, sessionId, isNewSession };
}

export function initTracking() {
  if (!isTrackingEnabled()) return;
  // trackEvent() calls ensureIds() itself — don't call it here too, or the
  // session will already exist by the time trackEvent checks isNewSession,
  // and the first-touch UTM/gclid capture below gets silently skipped.
  trackEvent("page_view");
}

export function clearTracking() {
  deleteCookie(COOKIE_VID);
  deleteCookie(COOKIE_SID);
}

// Purely aggregate, anonymous page-load ping — fires on every page view
// regardless of cookie consent (unlike trackEvent below), so admins can see
// that non-consenting traffic exists without ever identifying it. No ids,
// no cookies read or written here.
export function pingPageViewCount() {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/admin")) return;

  const body = JSON.stringify({ consented: isTrackingEnabled() });
  if (typeof fetch === "function") {
    fetch("/api/track/pageview-count", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  }
}

export function trackEvent(eventType, payload = {}) {
  if (typeof window === "undefined" || !isTrackingEnabled()) return;
  // Admin pages are internal tooling, never a visitor's own journey.
  if (window.location.pathname.startsWith("/admin")) return;

  const { visitorId, sessionId, isNewSession } = ensureIds();
  const { buttonLocation, ...metadata } = payload;

  const body = {
    visitorId,
    sessionId,
    eventType,
    pageUrl: window.location.href,
    pageTitle: document.title,
    buttonLocation: buttonLocation || null,
    metadata,
    session: isNewSession ? captureUtmAndSource() : undefined,
  };

  const json = JSON.stringify(body);

  // fetch+keepalive first: tel:/mailto: links hand off to another app on
  // click, and sendBeacon has been unreliable in that exact handoff on some
  // Android Chrome versions (the request gets queued but never leaves).
  // keepalive fetch survives the same page-hide/unload scenarios.
  if (typeof fetch === "function") {
    fetch("/api/track/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json,
      keepalive: true,
    }).catch(() => {
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/track/event", new Blob([json], { type: "application/json" }));
      }
    });
  } else if (navigator.sendBeacon) {
    navigator.sendBeacon("/api/track/event", new Blob([json], { type: "application/json" }));
  }
}

// Canonical click-to-call handler: fires GA4 + first-party tracking, then
// delays the tel: handoff just long enough for the request above to have
// left the browser, instead of racing it against the app switch.
export function trackPhoneClick(event, { phoneNumber, location }) {
  event.preventDefault();

  if (typeof window.gtag === "function") {
    window.gtag("event", "click_call", {
      contact_method: "phone",
      phone_location: location,
      phone_number: phoneNumber,
      link_type: "tel",
      page_location: window.location.href,
      page_title: document.title,
      value: 500,
      currency: "EUR",
    });
  }

  trackEvent("click_call", { buttonLocation: location, phoneNumber });

  window.setTimeout(() => {
    window.location.href = `tel:${phoneNumber}`;
  }, 120);
}
